<template>
  <v-container fluid>
    <v-card outlined class="flex-column">
      <ValidationObserver ref="form">
        <v-form @submit.prevent="onSubmit()">
          <v-container>
            <v-card outlined class="mx-auto my-6 pa-3">
              <div class="d-flex justify-space-between align-end">
                <v-card-title>{{ $t('deliveryMethod.globalSettings') }}</v-card-title>
              </div>

              <v-switch v-model="currentDelivery.activate" :label="$t('deliveryMethod.active')" />
              <v-row>
                <v-col cols="12" lg="6" xl="6">
                  <ValidationProvider rules="required" name="delivery_method_name" v-slot="{ errors }">
                    <v-text-field
                      v-model="currentDelivery.name"
                      type="string"
                      :label="$t('deliveryMethod.name')"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" lg="6" xl="6">
                  <v-text-field
                    v-model="currentDelivery.delivery_method_external_id"
                    type="string"
                    :label="$t('deliveryMethod.externalReference')"
                    clearable
                  ></v-text-field>
                </v-col>
                <v-col cols="12" lg="6" xl="6">
                  <v-text-field
                    v-model="currentDelivery.delivery_method_erp_id"
                    type="string"
                    :label="$t('deliveryMethod.erpDeliveryMethodId')"
                    clearable
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6" xl="6">
                  <ValidationProvider rules="required" name="currency_code" v-slot="{ errors }">
                    <v-autocomplete
                      v-model="currentDelivery.currency_code"
                      :items="currencies"
                      :item-text="(item) => `${item.code} (${item.name})`"
                      item-value="code"
                      :label="$t('deliveryMethod.currency')"
                      class="d-flex justify-center align-center py-2"
                      dense
                      hide-details="auto"
                      :error-messages="errors"
                      outlined
                    ></v-autocomplete>
                  </ValidationProvider>
                </v-col>
              </v-row>

              <delivery-constraints
                class="mb-2"
                :loading="loading"
                defaultConstraints
                :deliveryConstraints="currentDelivery.delivery_constraints.by_default"
                :currency="currentDelivery.currency_code"
              ></delivery-constraints>
              <v-card outlined>
                <v-container fluid>
                  <v-data-table
                    :headers="deliveryConstraintsHeader"
                    :items="currentDelivery.delivery_constraints.conditional"
                    item-key="id"
                    :no-data-text="$t('deliveryMethod.noPriceLevel')"
                    mobile-breakpoint="768"
                    :loading="loading"
                    dense
                    :header-props="headerProps"
                    :hide-default-footer="true"
                  >
                    <template v-slot:top>
                      <v-toolbar flat dense>
                        <div>{{ $t('deliveryMethod.priceLevels') }}</div>
                        <v-spacer></v-spacer>
                        <v-btn
                          v-if="!addNewConditionalConstraint"
                          color="primary"
                          outlined
                          @click="openAddDeliveryConstraintModal"
                          :disabled="loading"
                        >
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </v-toolbar>
                    </template>

                    <template v-slot:item.price="{ item }">
                      <span v-if="[null, undefined, ''].includes(item.price)">
                        {{ $t('deliveryMethod.byDefault') }}
                      </span>
                      <span v-else>{{ item.price }} {{ currentDelivery.currency_code }}</span>
                    </template>

                    <template v-slot:item.delivery_delay="{ item }">
                      <span v-if="[null, undefined, ''].includes(item.delivery_delay)">par défaut</span>
                      <span v-else>{{ item.delivery_delay }} {{ $t('deliveryMethod.hours') }}</span>
                    </template>

                    <template v-slot:item.application_conditions.total_weight.min="{ item }">
                      <span v-if="[null, undefined, ''].includes(item.application_conditions.total_weight.max)">
                        <v-icon x-small>mdi-greater-than-or-equal</v-icon>
                        {{ item.application_conditions.total_weight.min }}
                      </span>
                      <span v-else>
                        {{ item.application_conditions.total_weight.min }} -
                        {{ item.application_conditions.total_weight.max }}
                      </span>
                      {{ $t('deliveryMethod.kilogramShort') }}
                    </template>

                    <template v-slot:item.application_conditions.total_price.min="{ item }">
                      <span v-if="[null, undefined, ''].includes(item.application_conditions.total_price.max)">
                        <v-icon x-small>mdi-greater-than-or-equal</v-icon>
                        {{ item.application_conditions.total_price.min }}
                      </span>
                      <span v-else>
                        {{ item.application_conditions.total_price.min }} -
                        {{ item.application_conditions.total_price.max }}
                      </span>
                      {{ currentDelivery.currency_code }}
                    </template>

                    <template v-slot:item.actions="{ item }">
                      <v-btn icon class="pl-0 pr-0" color="primary" @click="openModifyDeliveryConstraintModal(item)">
                        <v-icon>mdi-square-edit-outline</v-icon>
                      </v-btn>
                      <v-btn icon class="pl-0 pr-0" color="error" @click="openRemoveDeliveryConstraintModal(item)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                  <delivery-constraints-dialog
                    v-model="addNewConditionalConstraint"
                    class="mt-2"
                    :loading="loading"
                    :title="$t('deliveryMethod.priceLevels')"
                    areNewConstraints
                    :deliveryConstraints="newDeliveryConstraint"
                    :currency="currentDelivery.currency_code"
                    @add-delivery-constraint="addNewDeliveryConstraint($event)"
                    @cancel-add-delivery-constraint="closeDeliveryConstraintModal"
                  ></delivery-constraints-dialog>
                  <delivery-constraints-dialog
                    v-model="modifyConditionalConstraint"
                    class="mt-2"
                    :loading="loading"
                    :title="$t('deliveryMethod.priceLevels')"
                    :deliveryConstraints="selectedDeliveryConstraint"
                    :currency="currentDelivery.currency_code"
                    @remove-delivery-constraint="openRemoveDeliveryConstraintModal($event)"
                    @cancel-add-delivery-constraint="closeDeliveryConstraintModal"
                    @update-delivery-constraint="updateDeliveryConstraint($event)"
                  ></delivery-constraints-dialog>
                </v-container>
              </v-card>
              <!--            <v-select v-model="currentDelivery.carrier" item-value="carrierName"-->
              <!--                      :items="carriersFiltered" item-text="name" :label="$t('deliveryMethod.carrier')"-->
              <!--                      class="d-flex justify-center align-center py-2" dense hide-details="auto" outlined>-->
              <!--            </v-select>-->
            </v-card>
            <v-card outlined class="mx-auto my-6 pa-3">
              <div class="d-flex justify-space-between align-end">
                <v-card-title>{{ $t('deliveryMethod.eligibilityConditions') }}</v-card-title>
              </div>
              <v-row>
                <v-col cols="12" sm="6" :class="this.$vuetify.breakpoint.width < 600 ? 'py-0' : 'py-0 pr-1'">
                  <total-price-eligibility
                    class="mb-2"
                    ref="totalPriceEligibility"
                    :loading="loading"
                    :totalPrices="currentDelivery.eligibility_conditions.total_price"
                    :currency="currentDelivery.currency_code"
                  ></total-price-eligibility>
                </v-col>
                <v-col cols="12" sm="6" :class="this.$vuetify.breakpoint.width < 600 ? 'py-0' : 'py-0 pl-1'">
                  <total-weight-eligibility
                    class="mb-2"
                    ref="totalWeightEligibility"
                    :loading="loading"
                    :totalWeights="currentDelivery.eligibility_conditions.total_weight"
                  ></total-weight-eligibility>
                </v-col>
              </v-row>
              <countries-eligibility
                class="mb-2"
                :restrictions="currentDelivery.eligibility_conditions.countries"
                :loading="loading"
                @add-countries-restriction="addCountriesRestriction($event)"
                @remove-countries-restriction="openDeleteCountriesDialog($event)"
              ></countries-eligibility>
              <locations-eligibility
                class="mb-2"
                :eligibilityConditions="currentDelivery.eligibility_conditions"
              ></locations-eligibility>
              <delivery-types-eligibility
                class="mb-2"
                :loading="loading"
                :eligibilityConditions="currentDelivery.eligibility_conditions"
              ></delivery-types-eligibility>
              <channels-eligibility
                class="mb-2"
                :loading="loading"
                :eligibilityConditions="currentDelivery.eligibility_conditions"
              ></channels-eligibility>
            </v-card>
          </v-container>

          <v-row class="justify-center pb-7">
            <v-btn class="ma-2" :disabled="loading" color="primary" outlined type="submit">
              {{ isNewMethod ? $t('deliveryMethod.create') : $t('deliveryMethod.update') }}
            </v-btn>
            <v-btn
              v-if="!isNewMethod"
              :disabled="loading"
              @click="deleteMethodDialog = true"
              class="ma-2"
              color="primary"
              outlined
            >
              {{ $t('deliveryMethod.delete') }}
            </v-btn>
          </v-row>
        </v-form>
      </ValidationObserver>
    </v-card>
    <delete-countries-confirmation-dialog
      v-model="deleteCountriesDialog"
      :title="$t('deliveryMethod.confirmDeliveryAreaDeletion')"
      :text="$t('deliveryMethod.confirmDeliveryAreaDeletionText')"
      :loading="loading"
      @yes="removeCountriesEligibility"
      @no="closeDeleteCountriesDialog"
    ></delete-countries-confirmation-dialog>
    <delete-delivery-method-dialog
      v-model="deleteMethodDialog"
      :title="$t('deliveryMethod.confirmMethodDeletion')"
      :text="$t('deliveryMethod.confirmMethodDeletionText')"
      :loading="loading"
      @yes="deleteDeliveryMethod"
      @no="deleteMethodDialog = false"
    ></delete-delivery-method-dialog>
    <remove-delivery-constraint-dialog
      v-model="deleteDeliveryConstraintDialog"
      :title="$t('deliveryMethod.confirmRemoveConstraint')"
      :text="$t('deliveryMethod.confirmRemoveConstraintText')"
      :loading="loading"
      @yes="removeDeliveryConstraint"
      @no="deleteDeliveryConstraintDialog = false"
    ></remove-delivery-constraint-dialog>
  </v-container>
</template>

<script>
  import { ValidationProvider, ValidationObserver } from 'vee-validate'
  import countriesEligibility from '../../components/carriers/deliveryMethods/eligibility/CountriesEligibility.vue'
  import locationsEligibility from '../../components/carriers/deliveryMethods/eligibility/LocationsEligibility.vue'
  import deliveryTypesEligibility from '@/components/carriers/deliveryMethods/eligibility/DeliveryTypesEligibility'
  import channelsEligibility from '@/components/carriers/deliveryMethods/eligibility/ChannelsEligibility'
  import totalPriceEligibility from '@/components/carriers/deliveryMethods/eligibility/TotalPriceEligibility'
  import totalWeightEligibility from '@/components/carriers/deliveryMethods/eligibility/TotalWeightEligibility'
  import deliveryConstraints from '@/components/carriers/deliveryMethods/deliveryConstraints/DeliveryConstraints'
  import deliveryConstraintsDialog from '@/components/dialogs/DeliveryConstraintsDialog'
  import { EventBus } from '@/event-bus'
  const currencies = require('@/assets/currencies.json')
  import confirmationDialog from '@/components/ConfirmationDialog'

  export default {
    name: 'deliveryMethodDetails',
    components: {
      ValidationProvider,
      ValidationObserver,
      countriesEligibility,
      locationsEligibility,
      deliveryTypesEligibility,
      channelsEligibility,
      totalPriceEligibility,
      totalWeightEligibility,
      deleteCountriesConfirmationDialog: confirmationDialog,
      deleteDeliveryMethodDialog: confirmationDialog,
      removeDeliveryConstraintDialog: confirmationDialog,
      deliveryConstraints,
      deliveryConstraintsDialog,
    },
    props: {
      delivery: {
        type: Object,
        default: null,
      },
    },
    data() {
      return {
        currentDelivery: {
          name: '',
          activate: true,
          delivery_method_external_id: '',
          delivery_method_erp_id: '',
          currency_code: '',
          carrier: '',
          eligibility_conditions: {
            channels: [],
            countries: [],
            delivery_types: [],
            locations: [],
            total_price: {
              min: 0,
              max: undefined,
            },
            total_weight: {
              min: 0,
              max: undefined,
            },
          },
          delivery_constraints: {
            by_default: {
              delivery_delay: 1,
              price: 0,
            },
            conditional: [],
          },
          delivery_methods: [],
          delivery_zone: {
            enabled: false,
            restrictions: [],
          },
        },
        currencies: currencies,
        loading: false,
        isNewMethod: true,
        deleteCountriesDialog: false,
        indexCountriesToDelete: undefined,
        deleteMethodDialog: false,
        addNewConditionalConstraint: false,
        deleteDeliveryConstraintDialog: false,
        indexDeliveryConstraintToDelete: undefined,
        selectedDeliveryConstraint: {},
        newDeliveryConstraint: {},
        modifyConditionalConstraint: undefined,
        indexDeliveryConstraintToModify: undefined,
      }
    },
    computed: {
      deliveryConstraintsHeader() {
        return [
          {
            text: this.$t('deliveryMethod.price'),
            value: 'price',
          },
          {
            text: this.$t('deliveryMethod.deliveryDelay'),
            value: 'delivery_delay',
          },
          {
            text: this.$t('deliveryMethod.priceLimit'),
            value: 'application_conditions.total_price.min',
          },
          {
            text: this.$t('deliveryMethod.weightLimit'),
            value: 'application_conditions.total_weight.min',
          },
          {
            text: '',
            value: 'actions',
            sortable: false,
          },
        ]
      },
      headerProps() {
        return {
          sortByText: this.$t('trigger.sortByText'),
        }
      },
    },
    mounted() {
      if (this.$route.params.id && this.$route.params.id !== 'add') {
        this.isNewMethod = false
        if (this.delivery && this.delivery._id === this.$route.params.id) {
          this.currentDelivery = this.delivery
        } else {
          this.get()
        }
      }
    },
    methods: {
      get() {
        this.loading = true
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        this.$Amplify.API.get('carriers', `/v1/int/delivery-methods/${this.$route.params.id}`, myInit)
          .then((res) => {
            this.currentDelivery = res
            this.$set(this.currentDelivery.eligibility_conditions, 'locations', res.eligibility_conditions.locations)
            this.$set(this.currentDelivery, 'currency_code', res.currency.code)
            this.loading = false
          })
          .catch(() => {
            this.loading = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('deliveryMethod.deliveryMethodsLoadingErrorMessage'),
              color: 'error',
            })
          })
      },
      create: function () {
        this.loading = true
        let deliveryMethod = JSON.parse(JSON.stringify(this.currentDelivery))
        this.convertNullMinMaxToUndefined(deliveryMethod)
        let myInit = {
          body: deliveryMethod,
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        this.$Amplify.API.post('carriers', '/v1/int/delivery-methods', myInit)
          .then(() => {
            this.loading = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('deliveryMethod.deliveryMethodCreated'),
              color: 'success',
            })
            this.$router.push('/delivery-methods')
          })
          .catch(() => {
            this.loading = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('deliveryMethod.errorCreatingMethod'),
              color: 'error',
            })
          })
      },
      update: function () {
        this.loading = true
        let deliveryMethod = JSON.parse(JSON.stringify(this.currentDelivery))
        this.convertNullMinMaxToUndefined(deliveryMethod)
        let myInit = {
          body: deliveryMethod,
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        this.$Amplify.API.patch('carriers', `/v1/int/delivery-methods/${this.$route.params.id}`, myInit)
          .then((res) => {
            this.loading = false
            this.currentDelivery = res
            this.$set(this.currentDelivery.eligibility_conditions, 'locations', res.eligibility_conditions.locations)
            this.$set(this.currentDelivery, 'currency_code', res.currency.code)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('deliveryMethod.deliveryMethodUpdated'),
              color: 'success',
            })
          })
          .catch(() => {
            this.loading = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('deliveryMethod.errorUpdatingMethod'),
              color: 'error',
            })
          })
      },
      deleteDeliveryMethod: function () {
        this.loading = true
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        this.$Amplify.API.del('carriers', `/v1/int/delivery-methods/${this.$route.params.id}`, myInit)
          .then(() => {
            this.loading = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('deliveryMethod.deliveryMethodDeleted'),
              color: 'success',
            })
            this.$router.push('/delivery-methods')
          })
          .catch(() => {
            this.loading = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('deliveryMethod.errorDeletingMethod'),
              color: 'error',
            })
          })
      },
      async onSubmit() {
        const validateForm = await this.$refs.form.validate()
        const validateTotalPriceEligibility = await this.$refs.totalPriceEligibility.validateForm()
        const validateTotalWeightEligibility = await this.$refs.totalWeightEligibility.validateForm()
        if (validateForm && validateTotalPriceEligibility && validateTotalWeightEligibility) {
          if (this.isNewMethod) {
            this.create()
          } else {
            this.update()
          }
        } else {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('deliveryMethod.incorrectFormFields'),
            color: 'error',
          })
        }
      },
      addCountriesRestriction(restrictions) {
        if (Array.isArray(this.currentDelivery.eligibility_conditions.countries)) {
          this.currentDelivery.eligibility_conditions.countries.unshift({
            sources: restrictions.sources,
            destinations: restrictions.destinations,
          })
        } else {
          this.currentDelivery.eligibility_conditions.countries = [
            { sources: restrictions.sources, destinations: restrictions.destinations },
          ]
        }
      },
      removeCountriesEligibility() {
        this.currentDelivery.eligibility_conditions.countries.splice(this.indexCountriesToDelete, 1)
        this.deleteCountriesDialog = false
      },
      openDeleteCountriesDialog(restriction) {
        this.indexCountriesToDelete = this.currentDelivery.eligibility_conditions.countries.indexOf(restriction)
        this.deleteCountriesDialog = true
      },
      closeDeleteCountriesDialog() {
        this.indexCountriesToDelete = undefined
        this.deleteCountriesDialog = false
      },
      convertNullToUndefined(value) {
        return [null, ''].includes(value) ? undefined : value
      },
      convertNullMinMaxToUndefined(deliveryMethod) {
        if (deliveryMethod.eligibility_conditions) {
          if (deliveryMethod.eligibility_conditions.total_price) {
            deliveryMethod.eligibility_conditions.total_price.min = this.convertNullToUndefined(
              deliveryMethod.eligibility_conditions.total_price.min
            )
            deliveryMethod.eligibility_conditions.total_price.max = this.convertNullToUndefined(
              deliveryMethod.eligibility_conditions.total_price.max
            )
          }
          if (deliveryMethod.eligibility_conditions.total_weight) {
            deliveryMethod.eligibility_conditions.total_weight.min = this.convertNullToUndefined(
              deliveryMethod.eligibility_conditions.total_weight.min
            )
            deliveryMethod.eligibility_conditions.total_weight.max = this.convertNullToUndefined(
              deliveryMethod.eligibility_conditions.total_weight.max
            )
          }
        }
        if (deliveryMethod.delivery_constraints?.conditional?.length) {
          for (let condition of deliveryMethod.delivery_constraints.conditional) {
            condition.price = this.convertNullToUndefined(condition.price)
            condition.delivery_delay = this.convertNullToUndefined(condition.delivery_delay)
            if (condition?.application_conditions?.total_price) {
              condition.application_conditions.total_price.min = this.convertNullToUndefined(
                condition.application_conditions.total_price.min
              )
              condition.application_conditions.total_price.max = this.convertNullToUndefined(
                condition.application_conditions.total_price.max
              )
            }
            if (condition?.application_conditions?.total_weight) {
              condition.application_conditions.total_weight.min = this.convertNullToUndefined(
                condition.application_conditions.total_weight.min
              )
              condition.application_conditions.total_weight.max = this.convertNullToUndefined(
                condition.application_conditions.total_weight.max
              )
            }
          }
        }
      },
      addNewDeliveryConstraint(newConstraint) {
        this.currentDelivery.delivery_constraints.conditional.unshift(newConstraint)
        this.addNewConditionalConstraint = false
      },
      openAddDeliveryConstraintModal() {
        this.$set(this.newDeliveryConstraint, 'price', 0)
        this.$set(this.newDeliveryConstraint, 'delivery_delay', 0)
        this.$set(this.newDeliveryConstraint, 'application_conditions', {
          total_price: {
            min: 0,
            max: undefined,
          },
          total_weight: {
            min: 0,
            max: undefined,
          },
        })
        this.addNewConditionalConstraint = true
      },
      openRemoveDeliveryConstraintModal(deliveryConstraint) {
        this.indexDeliveryConstraintToDelete =
          this.currentDelivery.delivery_constraints.conditional.indexOf(deliveryConstraint)
        this.deleteDeliveryConstraintDialog = true
      },
      openModifyDeliveryConstraintModal(deliveryConstraint) {
        this.indexDeliveryConstraintToModify =
          this.currentDelivery.delivery_constraints.conditional.indexOf(deliveryConstraint)
        let clonedDeliveryConstraints = this.deepClone(deliveryConstraint)
        this.$set(this.selectedDeliveryConstraint, 'price', clonedDeliveryConstraints.price)
        this.$set(this.selectedDeliveryConstraint, 'delivery_delay', clonedDeliveryConstraints.delivery_delay)
        this.$set(
          this.selectedDeliveryConstraint,
          'application_conditions',
          clonedDeliveryConstraints.application_conditions
        )
        this.modifyConditionalConstraint = true
      },
      closeDeliveryConstraintModal() {
        this.newDeliveryConstraint = {}
        this.selectedDeliveryConstraint = {}
        this.addNewConditionalConstraint = false
        this.modifyConditionalConstraint = false
      },
      removeDeliveryConstraint() {
        this.currentDelivery.delivery_constraints.conditional.splice(this.indexDeliveryConstraintToDelete, 1)
        this.indexDeliveryConstraintToDelete = undefined
        this.deleteDeliveryConstraintDialog = false
      },
      updateDeliveryConstraint(modifiedDeliveryConstraint) {
        this.currentDelivery.delivery_constraints.conditional.splice(
          this.indexDeliveryConstraintToModify,
          1,
          modifiedDeliveryConstraint
        )
        this.indexDeliveryConstraintToModify = undefined
        this.selectedDeliveryConstraint = {}
        this.modifyConditionalConstraint = false
      },
      deepClone(objectToClone) {
        if (typeof objectToClone === 'object') {
          let cloneTarget = Array.isArray(objectToClone) ? [] : {}
          for (const key in objectToClone) {
            cloneTarget[key] = this.deepClone(objectToClone[key])
          }
          return cloneTarget
        } else {
          return objectToClone
        }
      },
    },
  }
</script>
